<template>
    <page-layout
        :body-style="{ paddingTop: 0 }"
        :show-nav="isBriefNotNoneStatus || (isActivationArchived && !showSubmissionConfirmation)"
        :hero-image-url="!showSubmissionConfirmation && brief && brief.activation.image"
    >
        <g-loader v-if="isLoading" type="fixed" />
        <submission-confirmation
            v-else-if="isBriefCompleted && showSubmissionConfirmation"
            :brief="brief"
            :contact="contact"
        />
        <template v-else>
            <error-message-card v-if="errorMessage" class="margin-bottom-normal" :message="errorMessage" />
            <g-button
                v-if="brief.activated_at"
                class="pdf-ignore download-pdf-button"
                @click="$refs.pdfExportComponent.downloadPDF()"
            >
                <g-icon name="download" />
                Save Proposal as PDF
            </g-button>
            <template>
                <pdf-layout ref="pdfExportComponent">
                    <header v-if="pastBriefEndDate && !brief.activated_at" class="header g-mt-5 g-mb-5">
                        <g-level-item class="header-row">
                            <g-button size="small" class="pdf-ignore" @click="$root.go('sponsorships')"> Back</g-button>
                            <g-tag class="ended-tag" :theme="gray">Ended</g-tag>
                        </g-level-item>
                        <template v-if="pastBriefEndDate && !brief.activated_at">
                            <base-box class="g-mt-5 g-mb-3 past-brief-end-date-box">
                                <div slot="content">
                                    <g-typography tag="p">
                                        {{ brandName }} is no longer accepting submissions for proposals. Please contact
                                        <a :href="`mailto:${brief.brand.support_email}`">{{
                                            brief.brand.support_email
                                        }}</a>
                                        if you have any questions.
                                    </g-typography>
                                </div>
                            </base-box>
                        </template>
                        <div class="header-row g-mt-3">
                            <title-bar :title="brandName" />
                        </div>
                    </header>
                    <overview-header
                        v-else-if="!pastBriefEndDate || brief.activated_at"
                        :brief="brief"
                        :error-message="errorMessage"
                        :affiliate-links="affiliateLinks"
                        :discount-codes="discountCodes"
                        :restart-brief="restartBrief"
                        class="g-mt-5"
                    />
                    <component
                        :is="getComponent(stepName)"
                        v-for="stepName in stepOrder"
                        :key="`${briefStatus}.${stepName}`"
                        :brief="brief"
                        :contact="contact"
                        :brief-fields="briefFields"
                        :steps="steps"
                        :current-step="currentStep"
                        :is-preview="isPreview"
                        :is-brief-accepted="isBriefAccepted"
                        :is-brief-editable="isBriefEditable"
                        :brief-status="briefStatus"
                        :products="productDetails"
                    />
                </pdf-layout>
            </template>
            <template v-if="isBriefSubmittable" slot="footer">
                <g-button
                    :loading="false"
                    shape="round"
                    theme="live"
                    type="primary"
                    block
                    :disabled="!isComplete"
                    size="xlarge"
                    @click="handleSubmitBrief"
                >
                    Submit Proposal
                </g-button>
                <div class="powered-by-grin">
                    <powered-by-grin :clickable="true" />
                </div>
            </template>
            <template v-if="!isBriefSubmissionActive && !pastBriefEndDate" slot="footer" style="text-align: center">
                <div style="text-align: center; margin: 16px; padding: 16px">
                    <template v-if="beforeBriefStartDate">
                        <activation-brief-submission-countdown
                            :end-datetime="brief.activation.brief_submission_start_at"
                            @countdown-complete="() => briefRecomputeSubmissionDates()"
                            font-weight="bold"
                            italic
                        />
                    </template>
                </div>
                <g-button
                    :loading="false"
                    shape="round"
                    theme="live"
                    type="primary"
                    block
                    :disabled="true"
                    size="xlarge"
                >
                    Submit Proposal
                </g-button>
                <div class="powered-by-grin">
                    <powered-by-grin :clickable="true" />
                </div>
            </template>
        </template>
    </page-layout>
</template>

<script>
    import { get, findKey } from 'lodash';
    import TitleBar from '../../library/TitleBar';
    import PageLayout from '../../layouts/PageLayout';
    import GenericStepCard from './partials/GenericStepCard';
    import { formatUsPhoneNumber } from '../../../helpers/formatters';
    import ErrorMessageCard from './partials/ErrorMessageCard';
    import ActivationBriefMixin from './mixins/ActivationBriefMixin';
    import { getStepTitle } from '../../../helpers/activations/default-texts';
    import SubmissionConfirmation from './partials/SubmissionConfirmation';
    import StepCardContainerMixin from './mixins/StepCardContainerMixin';
    import { STEP_ORDERS } from '../../../constants/activation-brief-step-orders';
    import { ACTIVATION_BRIEF_STATUS_NONE } from '../../../constants/activation-brief';
    import PdfLayout from '../../../../../assets/app-v2/components/layouts/PdfLayout.vue';
    import OverviewHeader from './partials/OverviewHeader';
    import ActivationBriefSubmissionCountdown from './partials/steps/ActivationBriefSubmissionCountdown.vue';
    import useEventTracking from '../../../../../assets/src/composables/use-event-tracking';
    import { EVENT_NAME_ACTIVATION_PROPOSAL_SUBMIT} from "../../../constants/analytics-events";

    export default {
        name: 'ActivationBriefOverviewView',
        components: {
            OverviewHeader,
            ErrorMessageCard,
            PageLayout,
            TitleBar,
            GenericStepCard,
            SubmissionConfirmation,
            PdfLayout,
            ActivationBriefSubmissionCountdown
        },
        mixins: [ActivationBriefMixin, StepCardContainerMixin],
        data() {
            return {
                STEP_ORDERS,
                showSubmissionConfirmation: false
            };
        },
        props: {
            brief: {
                type: Object,
                default: null
            },
            contact: {
                type: Object,
                default: null
            },
            productDetails: {
                type: Array,
                default: () => []
            },
            affiliateLinks: {
                type: Array,
                default: () => []
            },
            discountCodes: {
                type: Array,
                default: () => []
            },
            briefFields: {
                type: Array,
                default: () => []
            },
            steps: {
                type: Object,
                default: null
            },
            currentStep: {
                type: Object,
                default: null
            },
            isLoading: {
                type: Boolean,
                default: false
            },
            isPreview: {
                type: Boolean,
                default: false
            },
            errorMessage: {
                type: String,
                default: null
            },
            restartBrief: {
                type: Function,
                required: true
            }
        },
        computed: {
            brandName() {
                return get(this, 'brief.brand.name', '');
            },
            currentStepName() {
                return get(this, 'currentStep.name', null);
            },
            address() {
                return this.contact.primary_address;
            },
            contactPhoneNumber() {
                if (this.contact.phone_country_code.toUpperCase() === 'US') {
                    return formatUsPhoneNumber(this.contact.phone_number_display);
                }

                return this.contact.phone_number_display;
            },
            isComplete() {
                if (!this.steps) {
                    return false;
                }

                return !findKey(this.steps, (step) => step.is_complete === false);
            },
            briefStatus() {
                if (!this.brief || !get(this, 'brief.status', false)) {
                    return ACTIVATION_BRIEF_STATUS_NONE;
                }
                return this.brief.status;
            },
            stepOrder() {
                return STEP_ORDERS[this.briefStatus];
            }
        },
        methods: {
            getStep(stepName) {
                return this.steps[stepName];
            },
            showStep(stepName) {
                return !!this.steps[stepName];
            },
            stepTitle(stepAlias) {
                return getStepTitle(this.brief, stepAlias);
            },
            handleSubmitBrief() {
                const events = useEventTracking();

                events.trackEvent(EVENT_NAME_ACTIVATION_PROPOSAL_SUBMIT, {
                    account_id: this.$root.account.id,
                    account_name: this.$root.account.company_name,
                    activation_id: this.brief.activation_id,
                    contact_id: this.contact.id,
                    source: 'creator'
                });
                this.$emit('complete-brief');
                this.showSubmissionConfirmation = true;
            }
        }
    };
</script>

<style lang="scss" scoped>
    .header {
        display: flex;
        flex-flow: column;
        width: 100%;
        justify-content: flex-start;

        &-row {
            display: flex;
            justify-content: space-between !important;
            width: 100%;
        }
    }

    .step-card-wrapper {
        margin-bottom: $g-spacing-5;
    }

    .no-margins {
        & ::v-deep p {
            margin: 0;
        }
    }

    .margin-bottom-tiny {
        margin-bottom: $g-spacing-1;
    }

    .margin-bottom-small {
        margin-bottom: $g-spacing-3;
    }

    .margin-bottom-normal {
        margin-bottom: $g-spacing-5;
    }

    .profile-info {
        padding-left: $g-spacing-3;
    }

    .powered-by-grin {
        text-align: center;
        margin-top: $g-spacing-4;
    }

    .download-pdf-button {
        margin-top: 1em;
        margin-bottom: 1em;
    }

    .ended-tag {
        height: 25px;
    }

    .past-brief-end-date-box {
        background-color: $g-color-primary-1;
    }
</style>
